/**
 * The app's BASE stylesheets for the print frame, without any print rules.
 *
 * Today's printed output is laid out under the app's base cascade (Tailwind
 * preflight, app fonts, theme variables), so the frame gets a copy of it.
 * Two rules keep that copy from re-importing the leak the frame removes
 * (TKT-00151/154: a lazy route's `@media print` rule blanked other prints):
 *
 * 1. Base layer only: the stylesheets present at app start (entry CSS + the
 *    font link), captured by captureBaseStyles() in main.tsx before any lazy
 *    route loads. Lazy-chunk CSS (e.g. POS) never reaches the frame.
 * 2. Every `@media print` block, `@page` rule and print-only @import is
 *    stripped, nested ones included. A frame's print rules come ONLY from the
 *    printed document's own CSS.
 */

let baseStyleNodes: ReadonlyArray<HTMLLinkElement | HTMLStyleElement> | null = null;

/** Record the app's base stylesheet nodes. Call once at startup (main.tsx). */
export function captureBaseStyles(): void {
  baseStyleNodes = Array.from(
    document.querySelectorAll<HTMLLinkElement | HTMLStyleElement>('link[rel~="stylesheet"], style'),
  );
}

/** Test hook: forget the captured base layer. */
export function resetBaseStylesForTest(): void {
  baseStyleNodes = null;
}

const isPrintMedia = (mediaText: string | undefined) => !!mediaText && /\bprint\b/i.test(mediaText);

type AnyRule = CSSRule & {
  cssRules?: CSSRuleList;
  media?: MediaList;
  selectorText?: string;
  style?: CSSStyleDeclaration;
};

/** CSS text of `rules` with every print-only rule removed (recursively). */
export function stripPrintRules(rules: CSSRuleList): string {
  return Array.from(rules as ArrayLike<AnyRule>)
    .map((rule) => {
      const text = rule.cssText;
      if (/^@page\b/i.test(text)) return "";
      if (rule.media && isPrintMedia(rule.media.mediaText)) return ""; // @media print, @import … print
      if (!rule.cssRules || rule.cssRules.length === 0 || !/@media|@page/i.test(text)) return text;
      const inner = stripPrintRules(rule.cssRules);
      // Nested style rule: keep its own declarations around the cleaned children.
      if (rule.selectorText !== undefined && rule.style) {
        return `${rule.selectorText} { ${rule.style.cssText} ${inner} }`;
      }
      return `${text.slice(0, text.indexOf("{"))}{${inner}}`; // @media screen, @supports, @layer, …
    })
    .filter(Boolean)
    .join("\n");
}

const escapeAttr = (value: string) =>
  value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

/** Keeps CSS text from closing the <style> element it is written into. */
export const escapeStyleText = (css: string) => css.replace(/<\/style/gi, "<\\/style");

/**
 * The base stylesheets as frame markup: same-origin sheets inlined with print
 * rules stripped; cross-origin sheets (Google Fonts, unreadable rules, no print
 * rules) linked. Empty when the base layer was never captured.
 */
export function baseStylesMarkup(): string {
  if (!baseStyleNodes) {
    console.warn("[print] captureBaseStyles() was not called; printing without app styles");
    return "";
  }
  // A base node's current sheet (a removed node has none); document order kept.
  const base = new Set(baseStyleNodes.map((node) => node.sheet).filter(Boolean));
  return Array.from(document.styleSheets)
    .filter((sheet) => !sheet.disabled && base.has(sheet))
    .map((sheet) => {
      const mediaText = sheet.media?.mediaText;
      if (isPrintMedia(mediaText)) return "";
      const media = mediaText ? ` media="${escapeAttr(mediaText)}"` : "";
      try {
        return `<style${media}>${escapeStyleText(stripPrintRules(sheet.cssRules))}</style>`;
      } catch {
        return sheet.href ? `<link rel="stylesheet" href="${escapeAttr(sheet.href)}"${media}>` : "";
      }
    })
    .join("");
}
